import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SolicitacoesService } from '../services/solicitacoesService';

const VisualizarSolicitacao: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [solicitacao, setSolicitacao] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const carregarSolicitacao = async () => {
      if (!id) {
        setError('ID não fornecido');
        setLoading(false);
        return;
      }

      try {
        const dados = await SolicitacoesService.buscarPorId(id);
        setSolicitacao(dados);
        setLoading(false);
      } catch (err) {
        console.error('Erro ao carregar solicitação:', err);
        setError('Solicitação não encontrada');
        setLoading(false);
      }
    };

    carregarSolicitacao();
  }, [id]);

  const formatarData = (data: string) => {
    if (!data) return '';
    // Converter string para Date usando fuso horário local para evitar problemas de UTC
    if (data.includes('T')) {
      const [ano, mes, dia] = data.split('T')[0].split('-').map(Number);
      const dataObj = new Date(ano, mes - 1, dia);
      return dataObj.toLocaleDateString('pt-BR');
    } else {
      const [ano, mes, dia] = data.split('-').map(Number);
      const dataObj = new Date(ano, mes - 1, dia);
      return dataObj.toLocaleDateString('pt-BR');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error || !solicitacao) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="text-center">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Erro</h1>
            <p className="text-gray-600">{error || 'Solicitação não encontrada'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-blue-600 rounded-t-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">UP DISTRIBUIDORA</h1>
              <p className="text-blue-100 mt-1">Comprovante de Agendamento</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{solicitacao.codigo}</div>
              <div className="text-sm text-blue-100">{formatarData(solicitacao.data_desejada)}</div>
            </div>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="bg-white rounded-b-2xl shadow-lg p-8">
          {/* Status */}
          <div className="mb-8">
            <div className={`inline-block px-6 py-3 rounded-full text-lg font-bold ${
              solicitacao.status.toLowerCase() === 'aprovado' ? 'bg-green-100 text-green-800' :
              solicitacao.status.toLowerCase() === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
              solicitacao.status.toLowerCase() === 'rejeitado' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              Status: {solicitacao.status.toUpperCase()}
            </div>
          </div>

          {/* Informações da Solicitação */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-3">Dados da Carga</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Fornecedor:</span>
                  <span className="font-medium">{solicitacao.cliente}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Produto:</span>
                  <span className="font-medium">{solicitacao.produto}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Peso:</span>
                  <span className="font-medium">{solicitacao.quantidade?.toLocaleString('pt-BR')} kg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Volume:</span>
                  <span className="font-medium">{solicitacao.volume || 'Não informado'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tipo de Carga:</span>
                  <span className="font-medium">{solicitacao.tipo_carga || 'Não informado'}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-3">Informações do Agendamento</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">ID da Solicitação:</span>
                  <span className="font-medium text-xs">{solicitacao.id.substring(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Filial:</span>
                  <span className="font-medium">{solicitacao.filial || 'Não informada'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Solicitante:</span>
                  <span className="font-medium">{solicitacao.criado_por || 'Não informado'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Data Solicitação:</span>
                  <span className="font-medium">{formatarData(solicitacao.criado_em)}</span>
                </div>
                {solicitacao.ordem_chegada && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ordem de Chegada:</span>
                      <span className="font-medium">#{solicitacao.ordem_chegada}</span>
                    </div>
                    {solicitacao.hora_ordem_chegada && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Horário:</span>
                        <span className="font-medium">{solicitacao.hora_ordem_chegada}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Horário de Funcionamento */}
          <div className="bg-blue-50 p-4 rounded-lg mb-8">
            <h3 className="font-bold text-gray-800 mb-3">⏰ Horário de Funcionamento</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Horário de Atendimento:</span>
                <span className="font-medium">08:00 às 14:00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Limite para Notas Fiscais:</span>
                <span className="font-medium text-red-600">Somente até as 10:00 horas da manhã</span>
              </div>
            </div>
          </div>

          {/* Termos e Regras */}
          <div className="bg-gray-50 p-4 rounded-lg mb-8">
            <h3 className="font-bold text-gray-800 mb-3">📋 Termos e Regras</h3>
            <div className="text-sm text-gray-700 space-y-3">
              <div>
                <strong>1) Fornecedor fora do estado - passagem obrigatória em posto fiscal:</strong>
                <p className="ml-4 mt-1">Toda carga de fornecedor localizado fora de Alagoas deve passar por um dos postos fiscais:</p>
                <ul className="ml-8 mt-1 list-disc">
                  <li>Delmiro Gouveia - Rodovia AL 423, KM 101</li>
                  <li>Novo Lino - Rodovia BR 101, KM 0</li>
                  <li>Porto Real do Colégio - Rodovia BR 101, KM 245</li>
                  <li>São José da Laje - Rodovia BR 104, KM 12</li>
                </ul>
              </div>
              <div>
                <strong>2) Código de barras obrigatório (DUN e EAN):</strong>
                <p className="ml-4 mt-1">Todos os produtos devem possuir código DUN e EAN, sem reutilização de códigos.</p>
              </div>
              <div>
                <strong>3) Mudança de fator:</strong>
                <p className="ml-4 mt-1">Qualquer alteração de fator deve ser informada previamente aos setores de Compras e Logística.</p>
              </div>
              <p className="font-medium text-red-600">O descumprimento pode acarretar em reagendamento ou devolução da carga.</p>
            </div>
          </div>

          {/* Observações */}
          {solicitacao.observacoes && (
            <div className="bg-yellow-50 p-4 rounded-lg mb-8">
              <h3 className="font-bold text-gray-800 mb-3">📝 Observações</h3>
              <p className="text-sm text-gray-700">{solicitacao.observacoes}</p>
            </div>
          )}

          {/* Rodapé */}
          <div className="border-t pt-6 text-center">
            <p className="text-gray-600 text-sm">Rodovia BR 104 KM 85, Rio Largo - AL, 57.100-000</p>
            <p className="text-gray-500 text-xs mt-2">
              Comprovante oficial de agendamento - Conservar para apresentação no dia
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualizarSolicitacao;
